import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { App as AntdApp, Avatar, Button, Input, Spin, Typography } from 'antd';
import { LeftOutlined, UserOutlined } from '@ant-design/icons';
import { fetchUserInfo, pickName, pickMobile, pickAvatar, saveUserProfile, type UlucuUserInfo } from '../api/user.js';
import { uploadImage } from '../api/upload.js';
import { useAuthStore } from '../stores/auth.js';
import { useRegion } from '../hooks/useRegion.js';

const PRIMARY = '#FF7043';

export default function ProfileEdit() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const region = useRegion();
	const { message } = AntdApp.useApp();
	const accountFromAuth = useAuthStore((s) => s.user?.account);
	const fileInputRef = useRef<HTMLInputElement | null>(null);
	const [info, setInfo] = useState<UlucuUserInfo | null>(null);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [name, setName] = useState('');
	const [mobile, setMobile] = useState('');
	const [email, setEmail] = useState('');
	const [avatarPreview, setAvatarPreview] = useState('');
	const [avatarFile, setAvatarFile] = useState<File | null>(null);

	useEffect(() => {
		void fetchUserInfo().then((u) => {
			setInfo(u);
			setName(pickName(u) || accountFromAuth || '');
			setMobile(pickMobile(u) || '');
			setEmail(u?.email || '');
		}).finally(() => setLoading(false));
	}, [accountFromAuth]);

	const onSave = async () => {
		setSaving(true);
		try {
		let avatarUrl: string | undefined;
		if (avatarFile && region === 'apac') {
			const uploaded = await withTimeout(uploadImage(avatarFile, 'avatar'), 30000, 'Upload timeout');
			if (!uploaded.ok || !uploaded.url) {
				message.error(uploaded.msg || t('native.common.operation_failed'));
				return;
			}
			avatarUrl = uploaded.url;
		}
		const result = await withTimeout(saveUserProfile({
			realname: name.trim(),
			mobile: mobile.trim(),
			email: email.trim(),
			...(avatarUrl ? { avatar: avatarUrl } : {}),
		}, region), 30000, 'Save timeout');
		setSaving(false);
		if (result.ok) {
			message.success(result.msg || t('common.success', { defaultValue: '保存成功' }));
			void fetchUserInfo().then((latest) => setInfo(latest));
			setAvatarFile(null);
			setAvatarPreview('');
		} else {
			message.error(result.msg || t('common.failed', { defaultValue: '保存失败' }));
		}
		} catch (error) {
			message.error(error instanceof Error ? error.message : t('native.common.operation_failed'));
		} finally {
			setSaving(false);
		}
	};

	const onAvatarFile = (file: File | undefined) => {
		if (!file) return;
		setAvatarFile(file);
		const reader = new FileReader();
		reader.onload = () => {
			if (typeof reader.result === 'string') setAvatarPreview(reader.result);
		};
		reader.readAsDataURL(file);
		if (fileInputRef.current) fileInputRef.current.value = '';
	};

	const avatarUrl = avatarPreview || pickAvatar(info);

	if (region === 'apac') {
		return (
			<div style={{ background: '#f5f5f5', minHeight: '100vh' }}>
				<div style={{ maxWidth: 720, margin: '0 auto' }}>
					<div style={{
						display: 'flex', alignItems: 'center',
						height: 48, padding: '0 8px', background: '#fff',
						borderBottom: '1px solid #eeeeee',
					}}>
						<Button type="text" shape="circle" icon={<LeftOutlined />} onClick={() => navigate(-1)} />
						<Typography.Text strong style={{ flex: 1, textAlign: 'center', fontSize: 17 }}>
							{t('mtab.profile.edit_personal_data')}
						</Typography.Text>
						<Button
							type="text"
							onClick={() => void onSave()}
							loading={saving}
							style={{ color: PRIMARY, fontWeight: 500, fontSize: 15, minWidth: 54 }}
						>
							{t('mtab.profile.save')}
						</Button>
					</div>

					{loading ? (
						<div style={{ padding: '60px 0', textAlign: 'center' }}>
							<Spin />
						</div>
					) : (
						<>
							<div style={{
								background: '#fff',
								padding: '24px 16px 20px',
								display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
							}}>
								<Avatar
									size={80}
									src={avatarUrl}
									icon={!avatarUrl && <UserOutlined />}
									style={{ background: avatarUrl ? undefined : '#d0d0d0', color: '#999' }}
								/>
								<input
									ref={fileInputRef}
									type="file"
									accept="image/*"
									style={{ display: 'none' }}
									onChange={(event) => onAvatarFile(event.currentTarget.files?.[0])}
								/>
								<Button
									type="link"
									style={{ color: PRIMARY, padding: 0 }}
									onClick={() => fileInputRef.current?.click()}
								>
									{t('mtab.profile.edit_avatar')}
								</Button>
							</div>

							<div style={{ background: '#fff', marginTop: 8 }}>
								<Row label={t('mtab.profile.name')}>
									<Input
										variant="borderless"
										value={name}
										onChange={(e) => setName(e.target.value)}
										style={{ textAlign: 'right' }}
									/>
								</Row>
								<Row label={t('mtab.profile.mobile_phone')}>
									<Input
										variant="borderless"
										value={mobile}
										onChange={(e) => setMobile(e.target.value)}
										style={{ textAlign: 'right' }}
										inputMode="tel"
									/>
								</Row>
								<Row label={t('mtab.profile.email_address')} last>
									<Input
										variant="borderless"
										value={email}
										onChange={(e) => setEmail(e.target.value)}
										style={{ textAlign: 'right' }}
										inputMode="email"
									/>
								</Row>
							</div>
						</>
					)}
				</div>
			</div>
		);
	}

	return (
		<div style={{ background: '#f5f5f5', minHeight: '100vh' }}>
		<div style={{ maxWidth: 720, margin: '0 auto' }}>
			<div style={{
				display: 'flex', alignItems: 'center',
				padding: '12px 12px', background: '#fff',
				borderBottom: '1px solid #f0f0f0',
			}}>
				<Button type="text" shape="circle" icon={<LeftOutlined />} onClick={() => navigate(-1)} />
				<Typography.Text strong style={{ flex: 1, textAlign: 'center', fontSize: 17 }}>
					{t('mtab.profile.edit_profile')}
				</Typography.Text>
				<Button
					type="text"
					onClick={() => void onSave()}
					loading={saving}
					style={{ color: PRIMARY, fontWeight: 500, fontSize: 15 }}
				>
					{t('mtab.profile.save')}
				</Button>
			</div>

			{loading ? (
				<div style={{ padding: '60px 0', textAlign: 'center' }}>
					<Spin />
				</div>
			) : (
				<>
					<div style={{
						background: '#fff',
						padding: '24px 16px 20px',
						display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
					}}>
						<Avatar
							size={80}
							src={avatarUrl}
							icon={!avatarUrl && <UserOutlined />}
							style={{ background: avatarUrl ? undefined : '#d0d0d0', color: '#999' }}
						/>
						<Button
							type="link"
							style={{ color: PRIMARY, padding: 0 }}
							onClick={() => message.info(t('mtab.profile.avatar_upload_pending', {
								defaultValue: '头像上传需要 multipart 上传代理，已从 APK 找到上传域名，待下一步接入。',
							}))}
						>
							{t('mtab.profile.edit_avatar')}
						</Button>
					</div>

					<div style={{ background: '#fff', marginTop: 8 }}>
						<Row label={t('mtab.profile.name')}>
							<Input
								variant="borderless"
								value={name}
								onChange={(e) => setName(e.target.value)}
								style={{ textAlign: 'right' }}
							/>
						</Row>
						<Row label={t('mtab.profile.mobile')}>
							<Input
								variant="borderless"
								value={mobile}
								onChange={(e) => setMobile(e.target.value)}
								style={{ textAlign: 'right' }}
								inputMode="tel"
							/>
						</Row>
						<Row label={t('mtab.profile.email')} last>
							<Input
								variant="borderless"
								placeholder={t('mtab.profile.email_placeholder')}
								value={email}
								onChange={(e) => setEmail(e.target.value)}
								style={{ textAlign: 'right' }}
								inputMode="email"
							/>
						</Row>
					</div>
				</>
			)}
		</div>
		</div>
	);
}

function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
	return new Promise<T>((resolve, reject) => {
		const timer = typeof window !== 'undefined' ? window : globalThis;
		const timeout = timer.setTimeout(() => reject(new Error(label)), ms);
		promise.then(
			(value) => {
				timer.clearTimeout(timeout);
				resolve(value);
			},
			(error: unknown) => {
				timer.clearTimeout(timeout);
				reject(error);
			},
		);
	});
}

function Row({ label, children, last }: { label: string; children: ReactNode; last?: boolean }) {
	return (
		<div style={{
			display: 'flex', alignItems: 'center',
			padding: '14px 16px',
			borderBottom: last ? 'none' : '1px solid #f0f0f0',
		}}>
			<Typography.Text style={{ fontSize: 15, color: '#333', minWidth: 60 }}>
				{label}
			</Typography.Text>
			<div style={{ flex: 1 }}>{children}</div>
		</div>
	);
}
